/**
 * Overpaying Scorecard capture form — /overpaying-scorecard, shown under the
 * score. Replaces the old inline LeadForm and the "email me the results" pill.
 *
 * The scorecard passes its inputs and result as `context` (see
 * OverpayingScorecard.jsx → buildLeadContext). Keys read here:
 *   score, category, debit_rate_pct, credit_rate_pct, auth_fee_gbp,
 *   terminal_rental_gbp_per_month, pci_fee_gbp_per_month, terminal_count,
 *   monthly_card_turnover_midpoint_gbp, contract_status (a
 *   CONTRACT_STATUS_OPTIONS label).
 * Everything in context also lands in the Lead's Note.
 */
import { CONTRACT_STATUS_OPTIONS, company, currentProvider, email, fullName, phone } from "./common";

const has = (v) => typeof v === "number" && Number.isFinite(v);

/** "Debit 0.8% · Credit 1.6% · Auth £0.05/txn · Terminal £25/mo · PCI £10/mo" — only what was entered. */
export function overpayingRatesText(ctx) {
  return [
    has(ctx.debit_rate_pct) ? `Debit ${ctx.debit_rate_pct}%` : null,
    has(ctx.credit_rate_pct) ? `Credit ${ctx.credit_rate_pct}%` : null,
    has(ctx.auth_fee_gbp) ? `Auth £${ctx.auth_fee_gbp}/txn` : null,
    has(ctx.terminal_rental_gbp_per_month) ? `Terminal £${ctx.terminal_rental_gbp_per_month}/mo` : null,
    has(ctx.pci_fee_gbp_per_month) ? `PCI £${ctx.pci_fee_gbp_per_month}/mo` : null,
  ].filter(Boolean).join(" · ");
}

const contractStatusCrm = (label) => CONTRACT_STATUS_OPTIONS.find((o) => o.label === label)?.crm;

const toolOverpaying = {
  id: "tool_overpaying",
  label: "Overpaying Scorecard",
  crm: { layout: "Standard", leadSource: "Website Calculator - Overpaying Scorecard" },
  fields: [
    [fullName, company],
    [email, { ...phone, help: "We'll only call to discuss your results — never spam." }],
    currentProvider,
  ],
  submitLabel: "Get my free review",
  consent: { variant: "standard" },
  success: {
    title: "Thank you",
    body: "We've got your score and details. We'll be in touch with your personalised review shortly.",
  },
  contextToCrm: (ctx) => ({
    Current_rates: overpayingRatesText(ctx),
    Monthly_Card_Turnovers: ctx.monthly_card_turnover_midpoint_gbp,
    Number_of_card_machines: ctx.terminal_count,
    Contract_Status: contractStatusCrm(ctx.contract_status),
  }),
  summary: (values, ctx) => {
    if (!has(ctx.score)) return "Overpaying Scorecard";
    const category = ctx.category ? ` (${ctx.category.toLowerCase()})` : "";
    return `Overpaying Scorecard: ${ctx.score}/100${category}`;
  },
};

export default toolOverpaying;
